import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import DashboardLayout from '@/components/DashboardLayout';

import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ProfilePage from '@/pages/ProfilePage';

import AdminOverview from '@/pages/admin/AdminOverview';
import WarehousesList from '@/pages/admin/WarehousesList';
import WarehouseDetail from '@/pages/admin/WarehouseDetail';
import PlatformBilling from '@/pages/admin/PlatformBilling';
import AuditLogs from '@/pages/admin/AuditLogs';

import WarehouseOverview from '@/pages/warehouse/WarehouseOverview';
import CustomersPage from '@/pages/warehouse/CustomersPage';
import InventoryPage from '@/pages/warehouse/InventoryPage';
import ProductMasterPage from "@/pages/warehouse/ProductMasterPage";
import CategoryMasterPage from "@/pages/warehouse/CategoryMasterPage";
import CompanyMasterPage from "@/pages/warehouse/CompanyMasterPage";
import ChargeMasterPage from '@/pages/warehouse/ChargeMasterPage';
import { DispatchList, DispatchDetail } from '@/pages/warehouse/DispatchPages';
import { InvoicesList, InvoiceDetail } from '@/pages/warehouse/InvoicePages';

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
  if (user.role === 'WAREHOUSE_ADMIN') return <Navigate to="/warehouse" replace />;
  return <Navigate to="/admin/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/admin/login" element={<LoginPage role="SUPER_ADMIN" />} />
          <Route path="/warehouse/login" element={<LoginPage role="WAREHOUSE_ADMIN" />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']} redirect="/admin/login">
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="warehouses" element={<WarehousesList />} />
            <Route path="warehouses/:id" element={<WarehouseDetail />} />
            <Route path="billing" element={<PlatformBilling />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          <Route
            path="/warehouse"
            element={
              <ProtectedRoute roles={['WAREHOUSE_ADMIN']} redirect="/warehouse/login">
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<WarehouseOverview />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="products" element={<ProductMasterPage />} />
            <Route path="categories" element={<CategoryMasterPage />} />
            <Route path="companies" element={<CompanyMasterPage />} />
            <Route path="charge-masters" element={<ChargeMasterPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="dispatch" element={<DispatchList />} />
            <Route path="dispatch/:id" element={<DispatchDetail />} />
            <Route path="invoices" element={<InvoicesList />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
